import i18nFactory from '@yandex-int/i18n';
import createReReSelector from 're-reselect';
import { createSelector } from 'reselect';

import { GridActionsMenuItems } from '../../components/GridActionsMenu/GridActionsMenu';
import { MinusPhraseLibraryMenuActionsContainerProps } from '../../containers/MinusPhraseLibraryMenuActionsContainer/MinusPhraseLibraryMenuActionsContainer';
import { mapMinusPhraseValidationResultApiData } from '../../mappers/minus-phrase/minus-phrase';
import { UrlHelpers } from '../../service/UrlHelpers';
import { ValidationResultItem } from '../../typings/common';
import { AccessRights, ValidationErrorData } from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { minusPhraseLibraryConstantsFragment } from '../../typings/graphql-grid';
import { minusPhraseLibraryAction } from '../../typings/grid';
import {
    MinusPhraseLibraryActions,
    MinusPhraseLibraryRow
} from '../../typings/minusPhraseLibrary';
import { State } from '../../typings/state';

import * as keyset from './minusPhraseLibrary.i18n';

const DEFAULT_MINUS_PHRASES_FORM_EDIT_ERROR: ValidationErrorData[] = [];

const i18n = i18nFactory(keyset);

export const minusPhraseLibraryActionsMenuSelector = createReReSelector(
    (state: State, props: MinusPhraseLibraryMenuActionsContainerProps) =>
        String(props.index),
    (state: State) => state.minusPhraseLibrary.gridData.total,
    (state: State, props: MinusPhraseLibraryMenuActionsContainerProps) =>
        props.actions,
    (state: State) => state.commonData.accessRights,
    (state: State) => state.commonData.constants,
    (state, props: MinusPhraseLibraryMenuActionsContainerProps) =>
        state.minusPhraseLibrary.gridData.data[props.index],
    (
        minusPhraseLibraryIndex: string,
        total: number,
        actions: MinusPhraseLibraryActions,
        accessRights: AccessRights,
        constants?: ConstData,
        minusPhraseLibraryRow?: MinusPhraseLibraryRow
    ): {
        actions: Array<GridActionsMenuItems>;
        events: Record<string, () => void>;
    } => {
        const isMinusPhraseAvailable = accessRights.canManageLibMinusWords;

        if (!minusPhraseLibraryRow) {
            return {
                actions: [],
                events: {}
            };
        }

        const { groupsCount } = minusPhraseLibraryRow;

        return {
            actions: [
                {
                    items: [
                        {
                            body: i18n('edit'),
                            type:
                                minusPhraseLibraryAction.editMinusPhraseLibrary,
                            disabled: !isMinusPhraseAvailable,
                            disabledTooltip: i18n('editable-tooltip')
                        },
                        {
                            body: i18n('create-by'),
                            type:
                                minusPhraseLibraryAction.createByMinusPhraseLibrary,
                            disabled:
                                Boolean(
                                    constants &&
                                        constants.minusPhraseLibraryConstants
                                            .maxLibraryPacksCount <= total
                                ) || !isMinusPhraseAvailable,
                            disabledTooltip: isMinusPhraseAvailable
                                ? i18n('create-tooltip')
                                : i18n('create-tooltip-disabled')
                        },
                        {
                            body: i18n('delete'),
                            type:
                                minusPhraseLibraryAction.deleteMinusPhraseLibrary,
                            disabled:
                                !isMinusPhraseAvailable || groupsCount !== 0,
                            disabledTooltip: isMinusPhraseAvailable
                                ? i18n('delete-tooltip')
                                : i18n('delete-tooltip-disabled')
                        }
                    ]
                }
            ],
            events: {
                [minusPhraseLibraryAction.editMinusPhraseLibrary]: (): void =>
                    actions.onEdit(minusPhraseLibraryIndex),
                [minusPhraseLibraryAction.createByMinusPhraseLibrary]: (): void =>
                    actions.onCopyAndCreate(minusPhraseLibraryIndex),
                [minusPhraseLibraryAction.deleteMinusPhraseLibrary]: (): void =>
                    actions.onDelete(minusPhraseLibraryIndex)
            }
        };
    }
)(
    (state: State, props: MinusPhraseLibraryMenuActionsContainerProps) =>
        props.index
);

export const selectMinusPhraseLibraryModalErrors = createSelector(
    (state: State) => state.minusPhraseLibrary.errors,
    (errors?: ValidationResultItem[]): Array<ValidationErrorData> => {
        return errors
            ? errors.map(
                  (error: ValidationResultItem): ValidationErrorData => {
                      return {
                          path: error.path,
                          code: error.code,
                          text: mapMinusPhraseValidationResultApiData({
                              ...error
                          } as ValidationResultItem)
                      };
                  }
              )
            : DEFAULT_MINUS_PHRASES_FORM_EDIT_ERROR;
    }
);

export const selectMinusPhrasesLibraryConstants = createSelector(
    (state: State) => state.commonData.constants,
    (constants: ConstData): minusPhraseLibraryConstantsFragment | undefined => {
        if (!constants) {
            return undefined;
        }

        return constants.minusPhraseLibraryConstants;
    }
);

export const selectMinusPhrasesLibraryUrl = createSelector(
    (state: State) => state.commonData.login,
    (login: string): string => {
        return UrlHelpers.getMinusPhrasesLibraryUrl(login);
    }
);
